<script setup lang="ts">
import { computed, onMounted, onUnmounted } from "vue"
import { useRoute } from "vue-router"
import { storeToRefs } from "pinia"
import EpciCommuneInput from "../components/menu/EpciCommune.vue"
import { useConfigStore, useEpciCommuneStore, useOnlineStore, useUserStore } from "~/store"
import { useNuxtApp } from "#app"

defineProps({
    titleOverride: {
        type: String,
        required: false,
        default: "",
    },
})

// Hooks & Plugins
const route = useRoute()
const { $translate } = useNuxtApp()

// Stores
const configStore = useConfigStore()
const onlineStore = useOnlineStore()
const userStore = useUserStore()
const epciCommuneStore = useEpciCommuneStore()
const accessToken = useCookie("access_token")
const adminOriginalToken = useCookie("admin_original_token")

if (!epciCommuneStore?.currentEpciCommune?.id) {
    epciCommuneStore.initCurrentEpciCommune()
}

// Refs from stores
const { online } = storeToRefs(onlineStore)
const { currentUser } = storeToRefs(userStore)

// Computed
const loading = computed(() => configStore?.globalLoading)
const maintenance = computed(() => configStore?.maintenance)
const isImpersonating = computed(() => adminOriginalToken.value)

// Methods
function beforeUnloadHandler(e) {
    if (window.navigator && window.navigator.onLine === false) {
        e.preventDefault()
        e.returnValue = ""
    }
}
const onlineHandler = () => onlineStore?.setOnline(true)
const offlineHandler = () => onlineStore?.setOnline(false)

function cleanedRouteName(name: any) {
    if (!name)
        return ""
    if (name === "statistiques")
        return ""
    const StringName = String(name)
    const nameUpdate = StringName.replace(/[^a-z ]/gi, "").replace("parametres", "parametres.")
    return nameUpdate === "index" ? "" : nameUpdate
}

function leaveImpersonation() {
    accessToken.value = adminOriginalToken.value
    adminOriginalToken.value = null
    window.location.href = "/utilisateurs"
}

// Lifecycle Hooks
onMounted(() => {
    configStore.setGlobalLoading(false)
    window.addEventListener("offline", offlineHandler)
    window.addEventListener("online", onlineHandler)
    window.addEventListener("beforeunload", beforeUnloadHandler)
})

onUnmounted(() => {
    window.removeEventListener("offline", offlineHandler)
    window.removeEventListener("online", onlineHandler)
})
</script>

<template>
    <v-layout>
        <MenuGlobalLoader :loading="loading">
            <template #loader>
                <svg viewBox="25 25 50 50">
                    <circle class="loader-path" cx="50" cy="50" r="20" fill="none" stroke="#636363" stroke-width="2" />
                </svg>
            </template>
            <template #logo>
                <div class="text-uppercase text-overline font-weight-bold" style="font-size: 16px!important">
                    LARCHIVISTE
                </div>
            </template>
        </MenuGlobalLoader>

        <MenuMaintenanceMessage v-if="maintenance">
            <template #logo>
                <img class="mx-auto" src="@/assets/images/logo.png" alt="logo">
            </template>
        </MenuMaintenanceMessage>

        <client-only>
            <template v-if="!maintenance">
                <v-system-bar v-if="isImpersonating" color="error" class="text-white d-flex justify-center py-2" height="40">
                    <v-icon icon="mdi-incognito" class="mr-2" />
                    <span>Vous êtes actuellement connecté en tant que <strong>{{ $currentUser()?.first_name }}</strong>.</span>
                    <v-btn size="small" variant="outlined" class="ml-4" @click="leaveImpersonation">
                        Quitter ce mode
                    </v-btn>
                </v-system-bar>
                <MenuNavigationDrawer ref="MenuNavigationDrawer" color="primary">
                    <template #menu="{ rail }">
                        <MenuNavigationSidebar ref="navigationSidebar" :rail="rail" />
                    </template>
                </MenuNavigationDrawer>

                <v-main id="main-content" class="bg-grey-lighten-4">
                    <header class="position-sticky top-0 bg-white elevation-1" style="z-index: 99;">
                        <MenuToolbar ref="Toolbar" burger-color="primary" @show-navigation="$refs.MenuNavigationDrawer.showNavigation()">
                            <template #title>
                                <span class="text-primary font-weight-bold">LARCHIVISTE</span>
                            </template>
                            <template #rightMenu>
                                <EpciCommuneInput />
                                <MenuUserProfile />
                            </template>
                        </MenuToolbar>
                    </header>

                    <div id="page-content" class="pa-4">
                        <div class="mb-4">
                            <h1 class="text-h4 font-weight-medium text-grey-darken-3 d-flex align-center">
                                <slot name="title">
                                    {{ $translate(cleanedRouteName(route.name)) || $translate(titleOverride) }}
                                </slot>
                            </h1>

                            <MenuGlobalAlert class="mt-4" />

                            <v-alert
                                v-if="!online && (!currentUser || currentUser.role === 'admin')"
                                type="warning"
                                variant="tonal"
                                class="mt-4 d-print-none border"
                                :closable="false"
                                icon="mdi-wifi-off"
                            >
                                Vous êtes hors-ligne, veuillez ne pas recharger la page.
                            </v-alert>
                        </div>
                        <slot /> <!-- OUTPUT PAGE -->
                        <Toaster ref="toaster" />
                    </div>
                    <MenuFooter />
                </v-main>
            </template>
        </client-only>
    </v-layout>
</template>
